// Vista de inventario — Tanda 5.
//
// Tabla compacta de todos los documentos del proyecto con filtros
// (fase, audiencia, cliente, estado, búsqueda libre), ordenamiento por
// columna y paginación de 50 filas. Clic en fila abre el mismo
// DocumentDetail que HomeView.

import { html } from 'htm/preact'
import { useEffect, useMemo, useState } from 'preact/hooks'

import {
  initInventory,
  getInventoryState,
  subscribeInventoryChange
} from '../lib/inventory-store.js'
import {
  initValidation,
  getDocumentEntry,
  getCache,
  subscribeValidation
} from '../lib/validation-store.js'
import { exportInventoryToXlsx } from '../lib/exporter.js'
import { showToast } from '../lib/toast-bus.js'
import { StatusBadge } from './StatusBadge.js'
import { DocumentDetail } from './DocumentDetail.js'

const PAGE_SIZE = 50

const FASES = [
  'Fase 0',
  'Fase 1',
  'Fase 2',
  'Fase 3',
  'Fase 4',
  'Fase 5',
  'Fase 6'
]
const AUDIENCIAS = [
  'Internos JMA',
  'Cliente',
  'Socios',
  'Carpeta AFAC',
  'Transversal'
]
const ESTADOS = [
  'Aprobado',
  'Pendiente',
  'Requiere ajuste',
  'Bloqueado',
  'Obsoleto',
  'Sin estado'
]

const COLUMNS = [
  { key: 'name', label: 'Nombre', width: '360px' },
  { key: 'fase', label: 'Fase', width: '80px' },
  { key: 'audiencia', label: 'Audiencia', width: '110px' },
  { key: 'cliente', label: 'Cliente', width: '130px' },
  { key: 'version', label: 'Versión', width: '80px' },
  { key: 'codigoEntregable', label: 'Código E-XX', width: '110px' },
  { key: 'estado', label: 'Estado', width: '120px' },
  { key: 'lastModified', label: 'Última modificación', width: '170px' },
  { key: 'modificadoPor', label: 'Modificado por', width: '180px' }
]

const SORT_GETTERS = {
  name: (f) => f.name || '',
  fase: (f) => f.metadata?.fase || '',
  audiencia: (f) => f.metadata?.audiencia || '',
  cliente: (f) => f.metadata?.cliente || '',
  version: (f) => f.metadata?.version || '',
  codigoEntregable: (f) => f.metadata?.codigoEntregable || '',
  estado: (f) => getDocumentEntry(f)?.estado || '',
  lastModified: (f) => f.lastModifiedDateTime || '',
  modificadoPor: (f) => getDocumentEntry(f)?.modificadoPor || ''
}

function emptyFilters() {
  return {
    fases: new Set(),
    audiencias: new Set(),
    cliente: '',
    estados: new Set(),
    search: ''
  }
}

function matchesFilters(file, filters, search) {
  const meta = file.metadata || {}
  if (filters.fases.size > 0 && !filters.fases.has(meta.fase || '')) return false
  if (filters.audiencias.size > 0 && !filters.audiencias.has(meta.audiencia || '')) return false
  if (filters.cliente && meta.cliente !== filters.cliente) return false
  if (filters.estados.size > 0) {
    const estado = getDocumentEntry(file)?.estado || 'Sin estado'
    if (!filters.estados.has(estado)) return false
  }
  if (search) {
    const haystack = `${file.name} ${meta.codigoEntregable || ''} ${file.relativePath}`.toLowerCase()
    if (!haystack.includes(search)) return false
  }
  return true
}

function sortFiles(files, sort) {
  const get = SORT_GETTERS[sort.column] || SORT_GETTERS.name
  const dir = sort.dir === 'desc' ? -1 : 1
  return [...files].sort((a, b) => {
    const av = String(get(a) || '')
    const bv = String(get(b) || '')
    if (av === bv) return 0
    // Empuja vacíos al final, sin importar dir.
    if (!av) return 1
    if (!bv) return -1
    return av.localeCompare(bv, 'es', { numeric: true, sensitivity: 'base' }) * dir
  })
}

function ChipMultiselect({ label, options, selected, onChange }) {
  return html`
    <div class="inventory__filter-group">
      <div class="inventory__filter-label">${label}</div>
      <div class="inventory__chips">
        ${options.map((opt) => {
          const isActive = selected.has(opt)
          return html`
            <button
              type="button"
              key=${opt}
              class=${`inventory__chip ${isActive ? 'inventory__chip--active' : ''}`}
              aria-pressed=${isActive ? 'true' : 'false'}
              onClick=${() => {
                const next = new Set(selected)
                if (isActive) next.delete(opt)
                else next.add(opt)
                onChange(next)
              }}
            >${opt}</button>
          `
        })}
      </div>
    </div>
  `
}

export function InventoryView() {
  const [inv, setInv] = useState(getInventoryState())
  const [filters, setFilters] = useState(emptyFilters)
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [sort, setSort] = useState({ column: 'name', dir: 'asc' })
  const [page, setPage] = useState(1)
  const [selectedFile, setSelectedFile] = useState(null)
  const [, forceRender] = useState(0)

  useEffect(() => {
    let cancelled = false
    const unsubInv = subscribeInventoryChange((next) => {
      if (!cancelled) setInv(next)
    })
    const unsubVal = subscribeValidation(() => {
      if (!cancelled) forceRender((v) => v + 1)
    })
    initInventory().catch((err) => {
      // eslint-disable-next-line no-console
      console.error('[InventoryView] initInventory falló:', err)
    })
    initValidation().catch((err) => {
      // eslint-disable-next-line no-console
      console.error('[InventoryView] initValidation falló:', err)
    })
    return () => {
      cancelled = true
      unsubInv()
      unsubVal()
    }
  }, [])

  const files = inv.data?.files || []
  const loadState =
    inv.state === 'ready' || (inv.data && inv.state !== 'error')
      ? 'ready'
      : inv.state === 'error'
        ? 'error'
        : 'loading'
  const error = inv.error

  // Debounce de la búsqueda libre (200ms).
  useEffect(() => {
    const t = window.setTimeout(() => {
      setDebouncedSearch(filters.search.trim().toLowerCase())
    }, 200)
    return () => window.clearTimeout(t)
  }, [filters.search])

  // Al cambiar filtros o búsqueda, vuelve a la primera página.
  useEffect(() => {
    setPage(1)
  }, [
    filters.fases,
    filters.audiencias,
    filters.cliente,
    filters.estados,
    debouncedSearch
  ])

  const clientes = useMemo(() => {
    const set = new Set()
    for (const f of files) {
      const c = f.metadata?.cliente
      if (c) set.add(c)
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'es'))
  }, [files])

  const filtered = useMemo(
    () => files.filter((f) => matchesFilters(f, filters, debouncedSearch)),
    [files, filters, debouncedSearch]
  )
  const sorted = useMemo(() => sortFiles(filtered, sort), [filtered, sort])

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE))
  const safePage = Math.min(page, totalPages)
  const start = (safePage - 1) * PAGE_SIZE
  const pageItems = sorted.slice(start, start + PAGE_SIZE)

  const handleHeaderClick = (column) => {
    setSort((prev) =>
      prev.column === column
        ? { column, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { column, dir: 'asc' }
    )
  }

  const handleClearFilters = () => setFilters(emptyFilters())

  const hasActiveFilters =
    filters.fases.size > 0 ||
    filters.audiencias.size > 0 ||
    filters.cliente !== '' ||
    filters.estados.size > 0 ||
    filters.search !== ''

  const describeFilters = () => {
    const parts = []
    if (filters.fases.size > 0) parts.push(`Fases: ${[...filters.fases].join(', ')}`)
    if (filters.audiencias.size > 0) parts.push(`Audiencias: ${[...filters.audiencias].join(', ')}`)
    if (filters.cliente) parts.push(`Cliente: ${filters.cliente}`)
    if (filters.estados.size > 0) parts.push(`Estados: ${[...filters.estados].join(', ')}`)
    if (debouncedSearch) parts.push(`Búsqueda: "${debouncedSearch}"`)
    return parts.length > 0 ? parts.join(' · ') : '(ninguno — inventario completo)'
  }

  const handleExport = () => {
    if (sorted.length === 0) {
      showToast({
        message: 'No hay documentos para exportar con los filtros actuales.',
        kind: 'warning'
      })
      return
    }
    try {
      const filename = exportInventoryToXlsx(sorted, getCache(), {
        filtersDescription: describeFilters()
      })
      showToast({
        message: `Inventario exportado: ${filename} (${sorted.length} documentos).`,
        kind: 'success'
      })
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[InventoryView] export falló:', err)
      showToast({
        message: 'No se pudo generar el archivo Excel. Ver consola.',
        kind: 'error'
      })
    }
  }

  if (loadState === 'loading') {
    return html`
      <div class="inventory-view">
        <section class="section">
          <h1 class="section__title">Inventario</h1>
          <p class="section__subtitle">Cargando documentos desde SharePoint…</p>
        </section>
      </div>
    `
  }

  if (loadState === 'error') {
    return html`
      <div class="inventory-view">
        <section class="section">
          <h1 class="section__title">Inventario</h1>
          <div class="notice notice--alert">
            <strong>Error.</strong> ${error}
          </div>
        </section>
      </div>
    `
  }

  return html`
    <div class="inventory-view">
      <section class="section">
        <h1 class="section__title">Inventario</h1>
        <p class="section__subtitle">
          Tabla completa de documentos del proyecto. Usá los filtros y la
          búsqueda para acotar resultados. Clic en una fila abre el detalle.
        </p>
      </section>

      <section class="inventory">
        <div class="inventory__filters">
          <div class="inventory__filters-header">
            <div class="inventory__filters-title">Filtros</div>
            ${hasActiveFilters
              ? html`
                  <button
                    type="button"
                    class="inventory__clear"
                    onClick=${handleClearFilters}
                  >Limpiar filtros</button>
                `
              : null}
          </div>
          <div class="inventory__filters-grid">
            <${ChipMultiselect}
              label="Fase"
              options=${FASES}
              selected=${filters.fases}
              onChange=${(next) => setFilters((f) => ({ ...f, fases: next }))}
            />
            <${ChipMultiselect}
              label="Audiencia"
              options=${AUDIENCIAS}
              selected=${filters.audiencias}
              onChange=${(next) => setFilters((f) => ({ ...f, audiencias: next }))}
            />
            <div class="inventory__filter-group">
              <label class="inventory__filter-label" for="filter-cliente">Cliente</label>
              <select
                class="inventory__select"
                id="filter-cliente"
                value=${filters.cliente}
                onChange=${(e) => setFilters((f) => ({ ...f, cliente: e.target.value }))}
              >
                <option value="">Todos</option>
                ${clientes.map(
                  (c) => html`<option key=${c} value=${c}>${c}</option>`
                )}
              </select>
            </div>
            <${ChipMultiselect}
              label="Estado"
              options=${ESTADOS}
              selected=${filters.estados}
              onChange=${(next) => setFilters((f) => ({ ...f, estados: next }))}
            />
            <div class="inventory__filter-group inventory__filter-group--full">
              <label class="inventory__filter-label" for="filter-search">Búsqueda</label>
              <input
                id="filter-search"
                type="search"
                class="inventory__search"
                placeholder="Buscar por nombre, código E-XX o ruta…"
                value=${filters.search}
                onInput=${(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
              />
            </div>
          </div>
        </div>

        <div class="inventory__summary">
          <span>
            Mostrando <strong>${sorted.length}</strong> de
            <strong>${files.length}</strong> documentos${totalPages > 1
              ? html`<span class="inventory__summary-meta"> · Página ${safePage} de ${totalPages}</span>`
              : null}
          </span>
          <button
            type="button"
            class="btn btn--secondary inventory__export"
            onClick=${handleExport}
            disabled=${sorted.length === 0}
            title=${`Exportar ${sorted.length} documento(s) a Excel`}
          >Exportar inventario</button>
        </div>

        ${sorted.length === 0
          ? html`
              <div class="inventory__empty">
                <div class="inventory__empty-title">
                  Ningún documento coincide con los filtros aplicados
                </div>
                <button
                  type="button"
                  class="btn btn--primary"
                  onClick=${handleClearFilters}
                >Limpiar filtros</button>
              </div>
            `
          : html`
              <div class="inventory__table-wrapper">
                <table class="inventory-table">
                  <colgroup>
                    ${COLUMNS.map(
                      (col) => html`<col key=${col.key} style=${`width: ${col.width}`} />`
                    )}
                  </colgroup>
                  <thead>
                    <tr>
                      ${COLUMNS.map((col) => {
                        const isSorted = sort.column === col.key
                        const dir = isSorted ? sort.dir : null
                        return html`
                          <th
                            key=${col.key}
                            scope="col"
                            aria-sort=${isSorted ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'}
                            onClick=${() => handleHeaderClick(col.key)}
                          >
                            <span class="inventory-table__head-label">${col.label}</span>
                            ${isSorted
                              ? html`<span class="inventory-table__sort-arrow" aria-hidden="true">${dir === 'asc' ? '▲' : '▼'}</span>`
                              : null}
                          </th>
                        `
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    ${pageItems.map((file) => {
                      const entry = getDocumentEntry(file)
                      const meta = file.metadata || {}
                      const isSelected = selectedFile?.id === file.id
                      return html`
                        <tr
                          key=${file.id || file.relativePath}
                          class=${isSelected ? 'inventory-table__row--selected' : ''}
                          onClick=${() => setSelectedFile(file)}
                        >
                          <td class="inventory-table__cell--name">${file.name}</td>
                          <td>${meta.fase || '—'}</td>
                          <td>${meta.audiencia || '—'}</td>
                          <td>${meta.cliente || '—'}</td>
                          <td>${meta.version || '—'}</td>
                          <td>${meta.codigoEntregable || '—'}</td>
                          <td><${StatusBadge} estado=${entry?.estado} size="sm" /></td>
                          <td>${file.lastModifiedDateTime ? new Date(file.lastModifiedDateTime).toLocaleString('es-MX') : '—'}</td>
                          <td class="inventory-table__cell--wrap">${entry?.modificadoPor || '—'}</td>
                        </tr>
                      `
                    })}
                  </tbody>
                </table>
              </div>
              ${totalPages > 1
                ? html`
                    <div class="inventory__pagination">
                      <button
                        type="button"
                        class="inventory__page-btn"
                        disabled=${safePage === 1}
                        onClick=${() => setPage((p) => Math.max(1, p - 1))}
                      >← Anterior</button>
                      <span class="inventory__page-info">
                        Página ${safePage} de ${totalPages}
                      </span>
                      <button
                        type="button"
                        class="inventory__page-btn"
                        disabled=${safePage === totalPages}
                        onClick=${() => setPage((p) => Math.min(totalPages, p + 1))}
                      >Siguiente →</button>
                    </div>
                  `
                : null}
            `}
      </section>

      ${selectedFile
        ? html`<${DocumentDetail} file=${selectedFile} onClose=${() => setSelectedFile(null)} />`
        : null}
    </div>
  `
}
